import {
  QueryClient,
  QueryClientProvider,
  defaultShouldDehydrateQuery,
} from "@tanstack/react-query";
import "./index.css";

import AppRoutes from "@/shared/routes";
import { BrowserRouter } from "react-router-dom";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "react-hot-toast";
import { ThemeProvider } from "@/ui/contexts/theme-provider.tsx";
import { SessionProvider } from "@/auth/contexts/auth-context.tsx";
import AxiosErrorHandler from "@/auth/contexts/axios-handler.tsx";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { ModalProvider } from "@/shared/lib/modal/modal-context.tsx";
import { safeStorage } from "@/shared/lib/storage/safe-storage";
import {
  shouldPersistQuery,
  PERSIST_MAX_AGE,
  PERSIST_BUSTER,
} from "@/shared/lib/storage/persist-filter";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      // Give every query a 30s freshness window and drop refetch-on-window-focus.
      // With React Query's defaults (staleTime 0 + refetchOnWindowFocus true),
      // every alt-tab back to a page fired a synchronized refetch burst across all
      // active queries (on /map: alerts + events + KPIs + video-around for every
      // machine at once). Live data keeps its own explicit `refetchInterval`
      // (ROLLING_WINDOW_OPTIONS, the video/alert polls), so it stays fresh
      // regardless; individual queries can still opt back in if they need it.
      staleTime: 1000 * 30, // 30 seconds
      refetchOnWindowFocus: false,
    },
  },
})

// `safeStorage` keeps a full localStorage from crashing the app via an unhandled
// QuotaExceededError. The dehydrate filter + maxAge keep the persisted cache
// small so it rarely fills in the first place (large/volatile queries — GPS,
// video, ML frames, alerts — are not persisted; see persist-filter).
const localStoragePersister = createSyncStoragePersister({
  storage: safeStorage,
})

persistQueryClient({
  queryClient,
  persister: localStoragePersister,
  maxAge: PERSIST_MAX_AGE,
  buster: PERSIST_BUSTER,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) =>
      defaultShouldDehydrateQuery(query) && shouldPersistQuery(query),
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <ModalProvider>
          <BrowserRouter>
            <AxiosErrorHandler>
              <ThemeProvider defaultTheme={"dark"}>
                  <AppRoutes />
                <Toaster/>
              </ThemeProvider>
            </AxiosErrorHandler>
          </BrowserRouter>
        </ModalProvider>
      </SessionProvider>
      <ReactQueryDevtools initialIsOpen />
    </QueryClientProvider>
  );
}

export default App;
